import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { Typeahead } from "@/components/Typeahead";
import { ApiError } from "@/lib/api";
import {
  classCodeName,
  searchSubmissionCodes,
  wageTagText,
  type CodeOption,
  type SubmissionCodeSearch,
} from "@/lib/submissions/brokerSubmissions";

// A single class-code typeahead SCOPED to the submission (PMS-S156/BRK-S065):
// searchSubmissionCodes returns only the codes the submission's product is in
// appetite for in the state. Extracted from the WC rating form so each
// exposure row owns one (BRK-S047, DRY). When the page is empty for a SCOPING
// reason (no state, or no in-appetite codes for the state) it explains why
// rather than showing a bare empty dropdown. A failed search is non-fatal.
//
// The debounce, the two-character floor and the cancellation live in the shared
// `Typeahead` (BRK-S138), a searchable select since BRK-S314: the chosen code
// reads "5403 — Carpentry" in the box. What stays here is the only thing that is about CLASS CODES: which
// endpoint to call, how a code reads, and what an empty page MEANS, which for a
// scoped search is never "no results".
//
// WC codes are rated by the state of each row's location, and bureau states
// have their own codes (CA → WCIRB), so a row that has a location searches it
// (PMS-S665, BRK-S291). A location the submission no longer has falls back to
// the mailing state and asks for the location again. A location not saved yet
// has no id, so its row searches by its state (BRK-S302). A move to another
// location, or another state, re-checks the chosen code there, and clears it,
// saying why, when that state doesn't offer it.

/** The row's location, one of the submission's own (PMS-S702): its id scopes
 *  the search, and its state names it when a move clears a code. One not
 *  saved yet has only its state, which the search scopes by instead. */
export interface CodeLocation {
  id?: string;
  state: string | null;
}

/** Said when PMS no longer has the row's location (a 404 on its search). */
export const LOCATION_GONE =
  "This row’s location is no longer on this submission, so these are the mailing state’s codes. Choose the row’s location again.";

function movedNote(from: string | null, to: string | null): string {
  return from && to
    ? `This class code is for ${from}. Choose one for ${to}.`
    : "This class code isn’t offered at the row’s new location. Choose another.";
}

export function ClassCodeField({
  label,
  value,
  onChange,
  submissionId,
  location,
  required,
  error,
}: {
  /** The field's visible label, and the search box's name. */
  label: string;
  value: CodeOption | null;
  onChange: (code: CodeOption | null) => void;
  /** The submission the search is scoped to (PMS-S156). */
  submissionId: string;
  /** The row's location; without one, PMS uses the mailing state. */
  location?: CodeLocation;
  required?: boolean;
  error?: string;
}) {
  const { authedRequest } = useAuth();
  const [locationGone, setLocationGone] = useState(false);
  const [moved, setMoved] = useState<string | null>(null);
  const locationId = location?.id;
  const locationState = location?.state ?? null;

  // One scoped search: the row's location when it has one, else the mailing
  // state. A 404 means the submission no longer has the location; that falls
  // back to the mailing state rather than failing the field.
  const scoped = useCallback(
    async (query: string, limit: number): Promise<SubmissionCodeSearch> => {
      if (!locationId) {
        return searchSubmissionCodes(authedRequest, submissionId, query, {
          limit,
          ...(locationState ? { state: locationState } : {}),
        });
      }
      try {
        const page = await searchSubmissionCodes(
          authedRequest,
          submissionId,
          query,
          { limit, locationId },
        );
        setLocationGone(false);
        return page;
      } catch (err) {
        if (!(err instanceof ApiError) || err.status !== 404) throw err;
        setLocationGone(true);
        return searchSubmissionCodes(authedRequest, submissionId, query, {
          limit,
        });
      }
    },
    [authedRequest, submissionId, locationId, locationState],
  );

  // Stable across renders — `Typeahead` uses it as an effect dependency, so a
  // fresh closure per render would restart the debounce on every render.
  const search = useCallback(
    async (query: string) => {
      const page = await scoped(query, 10);
      return {
        items: page.items,
        // Empty-with-reason (PMS-S156): a scoping emptiness gets an
        // explanation, never a bare "no results" — that would be a claim about
        // appetite nobody made. A plain search miss (reason null) falls through
        // to the shared default.
        emptyMessage:
          page.reason === "no_codes_for_state"
            ? `No eligible class codes for ${page.state ?? "this state"} on this product — contact us to confirm appetite.`
            : page.reason === "no_state"
              ? "Add the insured’s state on the profile step to see eligible class codes."
              : undefined,
      };
    },
    [scoped],
  );

  // The latest value and onChange, for the move check below: it runs once per
  // move, so a parent's re-render must not cancel it.
  const latest = useRef({ value, onChange });
  useEffect(() => {
    latest.current = { value, onChange };
  });

  const previous = useRef({ id: locationId, state: locationState });
  useEffect(() => {
    const before = previous.current;
    if (before.id === locationId && before.state === locationState) return;
    previous.current = { id: locationId, state: locationState };
    setMoved(null);
    setLocationGone(false);
    const chosen = latest.current.value;
    if (!chosen) return;
    let cancelled = false;
    scoped(chosen.code, 50).then(
      (page) => {
        if (cancelled || page.items.some((c) => c.id === chosen.id)) return;
        latest.current.onChange(null);
        setMoved(movedNote(before.state, locationState ?? page.state));
      },
      () => {
        // A failed check keeps the code: nothing showed it to be wrong.
      },
    );
    return () => {
      cancelled = true;
    };
  }, [locationId, locationState, scoped]);

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <Typeahead
        label={label}
        placeholder="Search by code or description"
        search={search}
        searchingLabel="Searching class codes…"
        selected={value}
        onSelect={(code) => {
          setMoved(null);
          onChange(code);
        }}
        keyOf={(c) => c.id}
        labelOf={(c) => {
          const name = classCodeName(c);
          return name ? `${c.code} — ${name}` : c.code;
        }}
        required={required}
        error={error}
        // A dual-wage code says which of its pair it is, in words, under the
        // code in the list and under the box once chosen (BRK-S394).
        detail={(value && wageTagText(value)) || undefined}
        renderOption={(c) => (
          <>
            <span className="tabular-nums font-medium text-brk-text">
              {c.code}{" "}
              <span className="font-sans text-brk-dim">({c.typeName})</span>
            </span>
            <span className="text-xs text-brk-dim">
              {c.friendlyName ?? c.description}
            </span>
            {wageTagText(c) && (
              <span className="text-xs font-medium text-brk-text">
                {wageTagText(c)}
              </span>
            )}
          </>
        )}
      />
      {moved && (
        <p role="status" className="text-xs text-brk-dim">
          {moved}
        </p>
      )}
      {locationGone && (
        <p role="status" className="text-xs text-brk-dim">
          {LOCATION_GONE}
        </p>
      )}
    </div>
  );
}
